import axios from 'axios';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom';
import ResearcherLikeCommentActions from './IndependentResearcher/ResearcherLikeCommentActions';
import MarketplaceFeedDetailsModal from './MarketplaceFeedDetailsModal';
import useMarketPlaceVerificationStatus from '../../hooks/useMarketPlaceVerificationStatus';
import FileViewer from './FileViewer';
const BASE_URL = process.env.REACT_APP_BASE_URL;
const MarketPlaceFeedHome = () => {
  const { isVerified } = useMarketPlaceVerificationStatus();
  // console.log("The map loading is:",isVerified)
  const navigate = useNavigate();
  const [feedPosts, setFeedPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  // console.log("The posts are:",feedPosts)
  const [type, setType] = useState("");
  // const [searchType, setSearchType] = useState("");
  const [post, setPost] = useState(null);
  const [open, setOpen] = useState(false);
  const [commentVersions, setCommentVersions] = useState({});
  const [expandedTitles, setExpandedTitles] = useState({});
  const pageRef = useRef(1);
  const observer = useRef(null);

  const toggleTitleExpanded = (postId) => {
    setExpandedTitles((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  // ---------------- FETCH POSTS ----------------
  const fetchFeedPosts = async (pageNum, isNewSearch = false) => {
    if (loading) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${BASE_URL}/marketplace/researcher/feed/?page=${pageNum}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'x-api-key': process.env.REACT_APP_X_API_KEY,
          },
        }
      );

      const newData = response.data.results;

      setFeedPosts((prev) =>
        isNewSearch || pageNum === 1 ? newData : [...prev, ...newData]
      );

      setHasMore(!!response.data.next);
      pageRef.current = pageNum + 1;

    } catch (error) {
      console.error('Error fetching feed posts:', error);

      if (pageNum === 1) {
        setFeedPosts([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchFeedPosts(1, true);
  }, []);

  // ---------------- INFINITE SCROLL ----------------
  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loading) {
            fetchFeedPosts(pageRef.current, false);
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // cleanup
  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  const updatePostInFeed = (updatedPost) => {
    setFeedPosts(prev =>
      prev.map(p =>
        p.id === updatedPost.id
          ? { ...p, like_count: updatedPost.like_count, is_liked: updatedPost.is_liked }
          : p
      )
    );
  };

  const updateArticleReads = (articleId, newReads) => {
    setFeedPosts(prev => prev.map(a => a.id === articleId ? { ...a, views: newReads } : a));
  };

  const refreshPostComments = (postId) => {
    setCommentVersions((previous) => ({
      ...previous,
      [postId]: (previous[postId] || 0) + 1,
    }));
  };


  // ---------------- UI ----------------
  return (
    <>
      {/* Empty state for no results */}
      {!loading && feedPosts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">
            No posts available
          </div>
        </div>
      )}

      {feedPosts?.map((post, index) => {
        const isLast = index === feedPosts.length - 1;

        return (
          <div
            key={`${post.id}-${post.research_type}`} // Better unique key
            ref={isLast ? lastPostRef : null}
            onClick={() => {
              if (isVerified) {
                window.history.pushState(
                  {},
                  "",
                  `/marketplace/feed/post/${post.id}?research_type=${post.research_type}&type=${post.publication_type}`
                );
                setPost({
                  id: post.id,
                  research_type: post.research_type,
                  publication_type: post.publication_type,
                });
                setOpen(true);
              }
            }}
            className="mb-4 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className={`p-6 w-full ${isVerified ? "cursor-pointer" : ''}`}>
              <div className="flex items-center gap-3 justify-between mb-4">
                <div className="flex items-center gap-3" onClick={(e) => {
                  if (isVerified) {
                    e.stopPropagation()
                    navigate(post?.university_data ? `/marketplace/university/${post?.university_data?.id}` : `/marketplace/independent_researcher/${post?.researcher}`)
                  }
                }}>
                  <img
                    src={post?.university_data ? post?.university_data.university_logo : post?.profile_data?.profile_picture}
                    alt="University"
                    className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"

                  />

                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">
                      {post.university_data ? post?.university_data?.university_name : post?.profile_data?.name}
                    </span>
                    <span className="text-xs text-gray-600 mt-0.5">
                      By <strong>{post.university_data ? post?.profile_data?.name : "Researcher"}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* TAGS */}
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs px-3 py-1 bg-gray-100 rounded-lg">
                  {post?.field_of_interest}
                </span>
                <span className="text-xs px-3 py-1 bg-blue-100 rounded-lg">
                  {post?.area_of_interest}
                </span>
              </div>

              {/* TITLE */}
              <div className="text-lg text-gray-900 mb-2 break-words">
                {expandedTitles[post.id] || post.name.length <= 120
                  ? post.name
                  : post.name.slice(0, 120) + "..."}

                {post.name.length > 120 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTitleExpanded(post.id);
                    }}
                    className="text-blue-600 ml-2 text-sm font-medium"
                  >
                    {expandedTitles[post.id] ? "Read less" : "Read more"}
                  </button>
                )}
              </div>

              {/* MEDIA */}

              {
                !isVerified && <p>
                  {post.summary}
                </p>
              }

              {
                !isVerified && <button
                  onClick={() => navigate('/marketplace')}
                  className="mt-6 w-full py-3 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center justify-center gap-2"
                >
                  Unlock or Join MarketPlace to View More
                </button>
              }



              {
                isVerified &&
                <>
                  <FileViewer publication_type={post?.publication_type} file={post?.file} name={post?.name} />
                </>
              }

              {/* ACTIONS */}
              {
                isVerified && <ResearcherLikeCommentActions
                  key={`${post.id}-comments-${commentVersions[post.id] || 0}`}
                  postId={post?.id}
                  type={post?.research_type}
                  is_Liked={post?.is_liked}
                  likeCount={post?.like_count}
                  views={post?.views}
                  postOwnerId={post?.profile_data?.id}
                  onLikeUpdate={updatePostInFeed}
                />
              }
            </div>
          </div>
        );
      })}

      {/* LOADING STATE */}
      {loading && (
        <div className="flex justify-center py-6">
          <div className="text-gray-500 text-sm">Loading...</div>
        </div>
      )}

      {/* Load more indicator */}
      {!loading && hasMore && feedPosts.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">Scroll to load more</div>
        </div>
      )}

      {/* End of list indicator */}
      {!loading && !hasMore && feedPosts.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">No more posts</div>
        </div>
      )}

      {/* MODAL */}
      <MarketplaceFeedDetailsModal
        id={post?.id}
        type={post?.publication_type}
        research_type={post?.research_type}
        isOpen={open}
        onLikeUpdate={updatePostInFeed}
        onCommentsUpdate={refreshPostComments}
        onClose={() => setOpen(false)}
        onReadsUpdate={updateArticleReads}
      />
    </>
  )
}

export default React.memo(MarketPlaceFeedHome)
